import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import type { PDFDocumentLoadingTask } from 'pdfjs-dist';
import { useAuthStore } from '@/shared/store/auth';
// Build "legacy" + worker legacy: incluye polyfills (Uint8Array.prototype.toHex,
// etc.) para navegadores viejos (Chrome/WebView de tablets). El build moderno
// asume APIs de JS muy nuevas y falla con "a.toHex is not a function".
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

interface Props {
  /** URL del endpoint que devuelve `application/pdf` (con auth Bearer). */
  url: string;
  title: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Muestra un PDF servido por un endpoint protegido (JWT `Authorization: Bearer`).
 * Se descarga con `fetch` autenticado y se **renderiza con PDF.js** a canvas —
 * NO con `<iframe>`, porque Chrome en Android/tablet no renderiza PDFs embebidos
 * en iframe (muestra un stub de descarga). El botón "Abrir/Descargar" usa el
 * `blob:` ya descargado (no requiere auth) y funciona en móvil.
 */
export function AuthenticatedPdfFrame({ url, title, className, style }: Props): JSX.Element {
  const accessToken = useAuthStore((s) => s.accessToken);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    let loadingTask: PDFDocumentLoadingTask | null = null;
    setIsLoading(true);
    setError(null);
    setBlobUrl(null);

    const run = async (): Promise<void> => {
      try {
        const res = await fetch(url, {
          headers:
            accessToken !== null && accessToken.length > 0
              ? { Authorization: `Bearer ${accessToken}` }
              : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status} — ${res.statusText}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);

        const data = await blob.arrayBuffer();
        if (cancelled) return;

        // Cast al tipo del paquete principal — el runtime del build legacy es
        // idéntico, solo cambia el target de compilación (con polyfills).
        const pdfjs = (await import('pdfjs-dist/legacy/build/pdf.mjs')) as typeof import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        loadingTask = pdfjs.getDocument({ data });
        const pdfDoc = await loadingTask.promise;
        if (cancelled) return;

        const container = pagesRef.current;
        if (container === null) return;
        container.replaceChildren();

        const width = Math.max(container.clientWidth || 600, 320);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        for (let i = 1; i <= pdfDoc.numPages; i += 1) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (width / base.width) * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          if (i < pdfDoc.numPages) canvas.style.marginBottom = '10px';
          const ctx = canvas.getContext('2d');
          if (ctx === null) continue;
          container.appendChild(canvas);
          await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        }
        if (!cancelled) setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'No fue posible cargar el PDF');
        setIsLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (createdUrl !== null) URL.revokeObjectURL(createdUrl);
      if (loadingTask !== null) void loadingTask.destroy();
    };
  }, [url, accessToken]);

  return (
    <div className={className} style={{ ...(style ?? {}), overflowY: 'auto', position: 'relative' }}>
      {/* Barra: abrir/descargar usando el blob (funciona en móvil/tablet). */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-surface px-3 py-2">
        <span className="text-[11px] text-text-muted truncate">{title}</span>
        {blobUrl !== null && (
          <a
            href={blobUrl}
            target="_blank"
            rel="noreferrer"
            download="consentimiento.pdf"
            className="flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline flex-shrink-0"
          >
            <Download className="w-3.5 h-3.5" /> Abrir / descargar
          </a>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-text-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Generando PDF…
        </div>
      )}

      {error !== null && (
        <div className="p-4">
          <div className="rounded-md bg-danger-soft border border-red-200 text-red-800 text-sm p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div>{error}</div>
              {blobUrl !== null && (
                <a
                  href={blobUrl}
                  target="_blank"
                  rel="noreferrer"
                  download="consentimiento.pdf"
                  className="font-semibold underline mt-1 inline-block"
                >
                  Descargar el PDF
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div ref={pagesRef} className="p-2" />
    </div>
  );
}
